import {BehaviorSubject} from "rxjs/BehaviorSubject";

export interface StateSubjectState {
  key: string;
  from?: string[]; // empty = any
  cooldown?: number; // ms
}

export interface StateSubjectStateInfo extends StateSubjectState {
  key: string;
  from: string[]; // empty = any
  cooldown: number; // ms
  lastUsed: number;
}

export class StateSubject extends BehaviorSubject<string> {
  private stateDefinitions: {[key: string]: StateSubjectStateInfo} = {};

  /**
   * Creates a new {ConditionalBehaviorSubject}
   * @param {string} value to start with
   * @param {StateSubjectState[]} stateDefinitions
   *   (default is ConditionalBehaviorSubject.defaultCondition)
   */
  constructor(value: string, stateDefinitions: StateSubjectState[]) {
    super(value);

    for (const stateDefinition of stateDefinitions) {
      const key = stateDefinition.key;
      this.stateDefinitions[key] = {
        key,
        from: stateDefinition.from || [],
        cooldown: stateDefinition.cooldown || 0,
        lastUsed: 0,
      };
    }
  }

  private canGoTo(key: string) {
    if (!this.stateDefinitions[key]) return false;
    if (key === this.value) return false;

    const to = this.stateDefinitions[key];

    const cooldownOk = !to.cooldown ||
      !to.lastUsed ||
      to.cooldown <= (new Date().getTime() - to.lastUsed);

    const fromOk = !to.from.length || to.from.includes(this.value);

    return cooldownOk && fromOk;
  }

  private updateHistory(key) {
    this.stateDefinitions[key].lastUsed = new Date().getTime();
  }

  /**
   * Send a new value to this {StateSubject}
   * @param {string} value to send
   * @returns {boolean} whether the value was accepted/updated
   */
  next(value: string): boolean {
    if (this.canGoTo(value)) {
      this.updateHistory(value);
      super.next(value);
      return true;
    }

    return false;
  }
}
